"use client";

import { useCallback, useId, useState } from "react";
import { motion } from "framer-motion";
import Carousel from "~/componentsDS/Carousel";
import { CarouselApi, CarouselContentVisible, CarouselItem } from "~/components/ui/carousel";
import { cn } from "~/lib/utils";
import { useTrackEvent } from "~/hooks/useTrackEvent";
import { GTM_EVENT_NAMES } from "~/constants/gtmEvents";
import HormoneSupportOestraCardMobile from "./HormoneSupportOestraCardMobile";
import HormoneSupportOestraCard from "./HormoneSupportOestraCard";
import HormoneSupportOtherCard from "./HormoneSupportOtherCard";
import { HormoneSupportComparisonContent } from "../../data/hormone-support-content";

const TAB_ITEMS = [
    { value: "oestra", label: "Oestra®" },
    { value: "other", label: "The Other Methods" },
];

const HormoneSupportComparison = ({ content }: { content: HormoneSupportComparisonContent }) => {
    const instanceId = useId();
    const sendEvent = useTrackEvent();
    const [activeTab, setActiveTab] = useState("oestra");
    const [emblaApi, setEmblaApi] = useState<CarouselApi>();

    const handleSelect = useCallback((api: CarouselApi) => {
        if (!api) return;

        const index = api.selectedScrollSnap() % 2;
        setActiveTab(TAB_ITEMS[index]?.value ?? "oestra");
    }, []);

    const handleTabClick = useCallback((tabValue: string, tabLabel: string) => {
        setActiveTab(tabValue);

        sendEvent({
            event: "user_interaction",
            value: {
                event_name: GTM_EVENT_NAMES.LP_INTERACTION,
                widget: "hrt_comparison",
                meta_data: {
                    click_text: tabLabel,
                },
            },
        });

        if (!emblaApi) return;

        const targetIndex = TAB_ITEMS.findIndex(t => t.value === tabValue);
        const currentIndex = emblaApi.selectedScrollSnap() % 2;

        if (currentIndex === targetIndex) return;

        if (targetIndex > currentIndex) {
            emblaApi.scrollNext();
        } else {
            emblaApi.scrollPrev();
        }
    }, [emblaApi, sendEvent]);

    return (
        <>
            <div className="md:hidden flex flex-col items-center">
                <div className="inline-flex rounded-full bg-gray200 p-2">
                    <div className="relative grid grid-cols-2 gap-1">
                        {TAB_ITEMS.map((tab) => {
                            const isActive = activeTab === tab.value;

                            return (
                                <button
                                    key={tab.value}
                                    type="button"
                                    aria-pressed={isActive}
                                    onClick={() => handleTabClick(tab.value, tab.label)}
                                    className={cn(
                                        "relative z-10 flex h-12 min-w-[120px] items-center justify-center rounded-full px-3 font-suisseBP text-center text-[18px] leading-none transition-colors duration-200",
                                        isActive
                                            ? "text-primary500"
                                            : "text-gray400"
                                    )}
                                >
                                    {isActive ? (
                                        <motion.div
                                            layoutId={`${instanceId}-active-pill`}
                                            className="absolute inset-0 rounded-full bg-white shadow-[0_1px_4px_rgba(25,34,61,0.12)]"
                                            transition={{
                                                type: "spring",
                                                stiffness: 400,
                                                damping: 32,
                                                mass: 0.8,
                                            }}
                                        />
                                    ) : null}

                                    <span className="relative z-10 whitespace-nowrap">
                                        {tab.label}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                <Carousel
                    loop={true}
                    opts={{
                        align: "center",
                        skipSnaps: false,
                        dragFree: false
                    }}
                    dotCount={2}
                    setApi={setEmblaApi}
                    onSelect={handleSelect}
                    className="w-full mt-6"
                >
                    <CarouselContentVisible className="-ml-2">
                        <CarouselItem className="pl-2 basis-[360px]">
                            <HormoneSupportOestraCardMobile content={content.oestraMobile} />
                        </CarouselItem>

                        <CarouselItem className="pl-2 basis-[360px]">
                            <HormoneSupportOtherCard content={content.other} />
                        </CarouselItem>
                    </CarouselContentVisible>
                </Carousel>
            </div>

            <div className="hidden md:block">
                <div className="w-full grid gap-5 md:grid-cols-[67.3%_32.7%]">
                    <HormoneSupportOestraCard content={content.oestraDesktop} />
                    <HormoneSupportOtherCard content={content.other} />
                </div>
            </div>
        </>
    );
};

export default HormoneSupportComparison;
